import { useState } from "react";
import { toast } from "sonner";
import { X, UserCircle, Loader2, Trash2, KeyRound } from "lucide-react";
import api, { errMsg } from "@/lib/api";

// Account details, password change and permanent deletion (password required).
export default function AccountDialog({ open, onClose, user, onDeleted, mustChange, onPasswordChanged }) {
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const close = () => { setConfirming(false); setPassword(""); onClose(); };
  const changePw = async (e) => {
    e.preventDefault();
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Ο κωδικός άλλαξε. Οι άλλες συνεδρίες αποσυνδέθηκαν.");
      setCurPw(""); setNewPw("");
      onPasswordChanged?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setPwBusy(false); }
  };
  const del = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.delete("/auth/me", { data: { password } });
      toast.success("Ο λογαριασμός διαγράφηκε.");
      onDeleted();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid="account-dialog"
      style={{ background: "rgba(245,245,247,0.6)", backdropFilter: "saturate(180%) blur(20px)", WebkitBackdropFilter: "saturate(180%) blur(20px)" }} onClick={close}>
      <div className="card w-full max-w-md p-6 fadeup" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><UserCircle className="w-5 h-5 text-[var(--txt-2)]" />Λογαριασμός</h3>
          <button onClick={close} className="btn-ghost w-9 h-9 grid place-items-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-sm mb-1 font-medium">{user?.name}</div>
        <div className="text-sm text-[var(--txt-2)] mb-6">{user?.email}</div>

        {mustChange && (
          <p data-testid="account-must-change" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-4">
            Ο κωδικός σας είναι μικρότερος από 8 χαρακτήρες. Επιλέξτε νέο, μεγαλύτερο κωδικό.
          </p>
        )}
        <form onSubmit={changePw} className="space-y-3 mb-6" data-testid="account-password-form">
          <div className="label flex items-center gap-2"><KeyRound className="w-4 h-4" />Αλλαγή κωδικού</div>
          <input data-testid="account-current-password" type="password" required autoComplete="current-password" placeholder="Τρέχων κωδικός"
            value={curPw} onChange={(e) => setCurPw(e.target.value)} className="field w-full px-3.5 py-2.5 text-sm" />
          <input data-testid="account-new-password" type="password" required minLength={8} autoComplete="new-password" placeholder="Νέος κωδικός (τουλάχιστον 8 χαρακτήρες)"
            value={newPw} onChange={(e) => setNewPw(e.target.value)} className="field w-full px-3.5 py-2.5 text-sm" />
          <button data-testid="account-password-submit" disabled={pwBusy || !curPw || newPw.length < 8}
            className="btn-primary px-4 py-2.5 text-sm flex items-center gap-2">
            {pwBusy && <Loader2 className="w-4 h-4 animate-spin" />}Αλλαγή κωδικού
          </button>
        </form>

        {!confirming ? (
          <button data-testid="account-delete-start" onClick={() => setConfirming(true)}
            className="btn-ghost px-4 py-2.5 text-sm flex items-center gap-2 text-rose-700">
            <Trash2 className="w-4 h-4" />Διαγραφή λογαριασμού
          </button>
        ) : (
          <form onSubmit={del} className="space-y-3" data-testid="account-delete-form">
            <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
              Θα διαγραφούν οριστικά ο λογαριασμός σας, όλες οι υποθέσεις που παρακολουθείτε, οι ειδοποιήσεις και τα επαληθευμένα στοιχεία επικοινωνίας.
              Αν είστε διαχειριστής ομάδας, η διαχείριση περνά σε άλλο μέλος. Η ενέργεια δεν αναιρείται.
            </p>
            <div>
              <label className="label block mb-1.5">Κωδικός για επιβεβαίωση</label>
              <input data-testid="account-delete-password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} className="field w-full px-3.5 py-2.5 text-sm" />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => { setConfirming(false); setPassword(""); }} className="btn-ghost px-4 py-2.5 text-sm">Άκυρο</button>
              <button data-testid="account-delete-confirm" disabled={busy || !password}
                className="btn-primary px-4 py-2.5 text-sm flex items-center gap-2" style={{ background: "#be123c" }}>
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}Οριστική διαγραφή
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
